import type { FunctionComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { VehicleWidgetDialogName } from 'Components/names';
import type { TemplateFunction } from 'Components/types';
import { dialogOpened } from 'Core/actions/dialog';
import { clearVerifyFitmentRequest } from 'Core/actions/fitmentSearch/request';
import { vehicleDiscardingRequested } from 'Core/actions/fitmentSearch/vehicle';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchFieldsSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
  verifyFitmentFacetsSelector,
  verifyFitmentProductSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import { FacetCollection, Vehicle } from 'Models';
import { isVehicleChangedOnResponse } from 'Models/vehicle';
import type { VerifyFitsValue } from 'Modules/serverApi/types';

import { getClassesByLayoutState, useLayoutState } from '../vehicleWidget/useLayoutState';
import VerifyFitmentDropdowns, { type Params as VehicleDropdownsParams } from './verifyFitmentDropdowns';
import VerifyFitmentLabel, { type Params as VehicleLabelParams } from './verifyFitmentLabel';
import VerifyFitmentSpecifier, { type Params as VehicleSpecifierParams } from './verifyFitmentSpecifier';

type Params = VehicleDropdownsParams | VehicleLabelParams | VehicleSpecifierParams;

interface Props {
  template: TemplateFunction<Params>;
  name: string;
  isAlwaysColumnLayout: boolean;
  columnBreakpoint?: number;
  initCollapsed: boolean;
  isAutoVehicleSelectionDisabled?: boolean;
  globalVehicleDiscardEnabled?: boolean;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
}

const VerifyFitment: FunctionComponent<Props> = ({
  template,
  isAlwaysColumnLayout = false,
  columnBreakpoint,
  name,
  initCollapsed,
  isAutoVehicleSelectionDisabled,
  globalVehicleDiscardEnabled,
  useNativeDropdown,
  useSearchableDropdown,
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const dispatch = useDispatch();

  const [aboutToChange, setAboutToChange] = useState(false);

  const isVehicleSelected = useSelector(isVehicleSelectedSelector);

  const facets = useSelector(verifyFitmentFacetsSelector) ?? new FacetCollection();

  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const unselectedFacets = facets.filter((f) => !f.selection.length);

  const baseFields = useSelector(fitmentSearchBaseFieldsSelector);
  const isAllBaseFieldSelected =
    facets.filter((facet) => baseFields.includes(facet.field) && !facet.selection.length).length === 0;

  const selectedVehicle = useSelector(selectedVehicleSelector);
  const responseVehicle = new Vehicle(facets.selection, fitmentFields);
  const isVehicleChanged = isVehicleChangedOnResponse(
    selectedVehicle,
    responseVehicle,
    !unselectedFacets.length,
  );

  const verifyFitmentProductData = useSelector(verifyFitmentProductSelector);
  const fitsTheVehicle = verifyFitmentProductData?.fitsTheVehicle;

  const layoutState = useLayoutState(rootRef, facets, columnBreakpoint, isAlwaysColumnLayout);
  const appendedClasses = [...getClassesByLayoutState(layoutState)];

  const labelTemplate = getLabelTemplateByFits(fitsTheVehicle ?? '');
  const isLockedTemplate =
    !aboutToChange && isVehicleSelected && !isVehicleChanged && !unselectedFacets.length;

  useEffect(
    function showLabelWhenVehicleChanges() {
      setAboutToChange(false);
    },
    [selectedVehicle],
  );

  if (
    !verifyFitmentProductData ||
    !Object.keys(verifyFitmentProductData).length ||
    fitsTheVehicle === 'nonVehicleProducts' ||
    (isLockedTemplate && !fitsTheVehicle)
  ) {
    return null;
  }

  const discardVehicle = () => {
    if (globalVehicleDiscardEnabled) {
      dispatch(vehicleDiscardingRequested({ globalVehicleDiscardEnabled }));
    } else {
      dispatch(clearVerifyFitmentRequest());
    }
  };
  const onChangeVehicle = () => setAboutToChange(true);
  const onSelectVehicle = () => setAboutToChange(false);
  const openDialog = () => dispatch(dialogOpened(VehicleWidgetDialogName, { withCurrentVehicle: true }));

  if (
    !aboutToChange &&
    isVehicleSelected &&
    isAllBaseFieldSelected &&
    unselectedFacets.length &&
    !labelTemplate
  ) {
    const props = {
      template,
      rootRef,
      appendedClasses,
      fitmentFields,
      unselectedFacets,
      vehicleString: responseVehicle.toString(),
      verifyFitmentProductData,
      onSelectVehicle,
      openDialog,
    };
    return <VerifyFitmentSpecifier {...props} />;
  }

  if (labelTemplate || isLockedTemplate) {
    const labelProps = {
      template,
      rootRef,
      templateName: labelTemplate || 'locked',
      appendedClasses,
      isVehicleSelected,
      responseVehicle,
      verifyFitmentProductData,
      discardVehicle,
      onChangeVehicle,
      openDialog,
    };

    return <VerifyFitmentLabel {...labelProps} />;
  }

  const dropdownsProps = {
    template,
    rootRef,
    appendedClasses,
    name,
    layoutState,
    facets,
    fitmentFields,
    responseVehicle,
    aboutToChange,
    allowToDiscardVehicle: facets.selection.length > 0,
    initCollapsed,
    isAllFieldsSelected: !unselectedFacets.length,
    isAutoVehicleSelectionDisabled,
    isVehicleSelected,
    useNativeDropdown,
    useSearchableDropdown,
    verifyFitmentProductData,
    discardVehicle,
    onSelectVehicle,
    openDialog,
  };
  return <VerifyFitmentDropdowns {...dropdownsProps} />;
};

function getLabelTemplateByFits(fits: VerifyFitsValue | '') {
  switch (fits) {
    case 'universal':
    case 'unknown':
      return fits;
    default:
      return null;
  }
}

export default VerifyFitment;
